import axios, { AxiosResponse } from 'axios';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { notifySuccess, notifyError } from '@helpers/index';
import { translateOutsideReact } from '@shared/i18n/intlInstance';
import { fetchSubscriptionsRequest } from '@modules/subscriptions/core/actions';
import { fetchCustomerRequest } from '@modules/customer/core/actions';
import { fetchOrderScheduleRequest } from '@modules/order-schedule/core/actions';

import {
  fetchUpsellsSuccess,
  fetchUpsellsFailure,
  addUpsellProductRequest,
  addUpsellProductSuccess,
  addUpsellProductFailure,
  removeUpsellProductRequest,
  removeUpsellProductSuccess,
  removeUpsellProductFailure,
  removeUpsellProductsBatchRequest,
  removeUpsellProductsBatchSuccess,
  removeUpsellProductsBatchFailure,
} from './actions';
import {
  FETCH_UPSELLS_REQUEST,
  ADD_UPSELL_PRODUCT_REQUEST,
  REMOVE_UPSELL_PRODUCT_REQUEST,
  REMOVE_UPSELL_PRODUCTS_BATCH_REQUEST,
} from './actionTypes';
import {
  UpsellsResponse,
  AddUpsellProductRequestPayload,
  RemoveUpsellProductRequestPayload,
} from './types';

const getUpsells = () => axios.get<UpsellsResponse>('/api/v2/products/upsell');

const addUpsellProduct = (subscriptionId: number, params: AddUpsellProductRequestPayload) =>
  axios.post<any>(`/api/v1/subscriptions/${subscriptionId}/add_upsell_product`, {
    subscription: {
      variantShopifyId: Number(params.variantShopifyId),
      quantity: params.quantity,
      price: params.price,
      isUpsell: params.isUpsell ?? true,
      ...(params.customAttributes ? { customAttributes: params.customAttributes } : {}),
    },
  });

const removeUpsellProduct = (subscriptionId: number, lineId: string) =>
  axios.delete<any>(`/api/v1/subscriptions/${subscriptionId}/remove_upsell_product/${lineId}`);

function* fetchUpsellsSaga() {
  try {
    const response: AxiosResponse<UpsellsResponse> = yield call(getUpsells);

    yield put(
      fetchUpsellsSuccess({
        products: response.data?.products ?? [],
      }),
    );
  } catch (e: unknown) {
    yield put(
      fetchUpsellsFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* addUpsellProductSaga(action: ReturnType<typeof addUpsellProductRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      addUpsellProduct(action.payload.subscriptionId, action.payload),
    );

    yield put(
      addUpsellProductSuccess({
        subscriptionId: action.payload.subscriptionId,
        data: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.subscriptionId }));
    notifySuccess(
      translateOutsideReact('SHOP.TOAST.ADDED', 'Produkt zur nächsten Bestellung hinzugefügt'),
    );
  } catch (e: unknown) {
    yield put(
      addUpsellProductFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SHOP.TOAST.ADD_FAILED', 'Produkt konnte nicht hinzugefügt werden'),
    );
  }
}

function* removeUpsellProductSaga(action: ReturnType<typeof removeUpsellProductRequest>) {
  try {
    const response: AxiosResponse<any> = yield call(() =>
      removeUpsellProduct(action.payload.subscriptionId, action.payload.lineId),
    );

    yield put(
      removeUpsellProductSuccess({
        subscriptionId: action.payload.subscriptionId,
        lineId: action.payload.lineId,
        data: response.data,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId: action.payload.subscriptionId }));
    yield put(fetchCustomerRequest());
    notifySuccess(
      translateOutsideReact('SHOP.TOAST.REMOVED', 'Produkt aus der nächsten Bestellung entfernt'),
    );
  } catch (e: unknown) {
    yield put(
      removeUpsellProductFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
    notifyError(
      translateOutsideReact('SHOP.TOAST.REMOVE_FAILED', 'Produkt konnte nicht entfernt werden'),
    );
  }
}

function* removeUpsellProductsBatchSaga(
  action: ReturnType<typeof removeUpsellProductsBatchRequest>,
) {
  const { subscriptionId, lineIds } = action.payload;
  const removed: string[] = [];

  try {
    // Sequential delete: Loop sometimes 409s when two deletes against the
    // same subscription land at the same instant. Sequencing also gives us
    // a clean way to surface the partial-success path on failure.
    for (const lineId of lineIds) {
      yield call(() => removeUpsellProduct(subscriptionId, lineId));
      removed.push(lineId);
    }

    yield put(
      removeUpsellProductsBatchSuccess({
        subscriptionId,
        lineIds: removed,
      }),
    );
    yield put(fetchSubscriptionsRequest());
    yield put(fetchOrderScheduleRequest({ subscriptionId }));
    yield put(fetchCustomerRequest());
    notifySuccess(
      translateOutsideReact('SHOP.TOAST.REMOVED', 'Produkt aus der nächsten Bestellung entfernt'),
    );
  } catch (e: unknown) {
    yield put(
      removeUpsellProductsBatchFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
        removedLineIds: removed,
      }),
    );

    // Refetch even on partial failure so anything we did successfully delete
    // disappears from the UI.
    if (removed.length > 0) {
      yield put(fetchSubscriptionsRequest());
      yield put(fetchOrderScheduleRequest({ subscriptionId }));
      yield put(fetchCustomerRequest());
    }

    notifyError(
      translateOutsideReact('SHOP.TOAST.REMOVE_FAILED', 'Produkt konnte nicht entfernt werden'),
    );
  }
}

export function* shopSaga() {
  yield all([
    takeLatest(FETCH_UPSELLS_REQUEST, fetchUpsellsSaga),
    takeLatest(ADD_UPSELL_PRODUCT_REQUEST, addUpsellProductSaga),
    takeLatest(REMOVE_UPSELL_PRODUCT_REQUEST, removeUpsellProductSaga),
    takeLatest(REMOVE_UPSELL_PRODUCTS_BATCH_REQUEST, removeUpsellProductsBatchSaga),
  ]);
}
